import React from "react";
import { useTranslation } from "react-i18next";
import {
  useAuthentication,
  useRetrieveCurrentPlanzone,
  useRetrieveFilterResources,
} from "@planzone/view-models";
import {
  Identifiable,
  Resource as R,
  resourcesListingFilter,
} from "@planzone/dto";
import { useWorkspaceContext } from "@scenes/workspaceContext";

import { UserIcon } from "@common/assets/userIcon";
import { ResourceAvatar } from "@common/resourceAvatar";
import { Flag, InfiniteScroll } from "@common/infiniteScroll";
import { Loader } from "../loader";
import { Checkbox } from "../checkbox";
import { Section } from "../section";
import { Props } from "./types";
import { Resource } from "./resource";
import styles from "./styles.scss";

const SEARCH_DELAY = 300;

// Lists the planzone resources, or the project members inside a project, searched and paginated by the back.
// "Everyone" is no resource picked: the whole list is never loaded to be picked.
function Resources({
  search,
  filter,
  setFilter,
  isOpen,
  hasOtherResourceCriteria,
}: Props) {
  const { t } = useTranslation();
  const { account } = useAuthentication();
  const { planzone } = useRetrieveCurrentPlanzone();
  const ctx = useWorkspaceContext();
  const project = ctx.type === "project" ? ctx.project : undefined;
  const {
    resources,
    isRequestPending,
    filter: resourcesFilter,
    totalPages,
    projectUuid,
    isDirty,
    isRequestFailure,
    retrieveFilterResources,
  } = useRetrieveFilterResources();
  const isSameList =
    projectUuid === (project?.uuid ?? null) &&
    (resourcesFilter.name ?? "") === search;
  const selectResources = (resources: Identifiable<R>[]) =>
    setFilter(filter ? [...filter, ...resources] : resources);
  const unSelectResources = (resources: Identifiable<R>[]) =>
    filter &&
    setFilter(
      filter.filter(
        (resource) => !resources.find(({ uuid }) => resource.uuid === uuid),
      ),
    );
  const isSelected = (resource: Identifiable<R>) =>
    !!filter && !!filter.find(({ uuid }) => resource.uuid === uuid);
  const isEveryoneSelected =
    (!filter || filter.length === 0) && !hasOtherResourceCriteria;

  // The list kept since the last opening is shown again, unless it was never loaded or another one is searched.
  React.useEffect(() => {
    if (isSameList && resourcesFilter.page > 0 && !isRequestFailure) return;

    const timeout = window.setTimeout(
      () =>
        retrieveFilterResources({
          planzone,
          project,
          filter: { ...resourcesListingFilter, name: search },
        }),
      SEARCH_DELAY,
    );

    return () => window.clearTimeout(timeout);
  }, [search, project?.uuid]);

  // A dirty list is reloaded as far as it was scrolled, as the time sheets do, also when it is searched again.
  React.useEffect(() => {
    isDirty &&
      isSameList &&
      !isRequestPending &&
      retrieveFilterResources({
        planzone,
        project,
        filter: {
          ...resourcesFilter,
          page: 0,
          size: Math.max(resources.length, resourcesListingFilter.size),
        },
      });
  }, [isDirty, isRequestPending, search, project?.uuid]);

  return (
    <div className={styles.resources}>
      <div
        className={styles.row}
        onClick={() => !isEveryoneSelected && setFilter([])}
      >
        <Checkbox isChecked={isEveryoneSelected} />
        <p className={styles.title}>
          {t("common.filterMenu.resources.everyone")}
        </p>
      </div>
      <div
        className={styles.row}
        onClick={() =>
          isSelected(account.resource)
            ? unSelectResources([account.resource])
            : selectResources([account.resource])
        }
      >
        <Checkbox isChecked={isSelected(account.resource)} />
        <div className={styles.avatar}>
          <ResourceAvatar resource={account.resource} />
        </div>
        <p className={styles.name}>
          {account.resource.name} ({t("common.filterMenu.resources.me")})
        </p>
      </div>
      <Section
        header={
          <div className={styles.header}>
            <div className={styles.iconBox}>
              <UserIcon className={styles.icon} />
            </div>
            <p className={styles.title}>
              {t("common.filterMenu.resources.title")}
            </p>
          </div>
        }
        list={
          <div className={styles.scrollPane}>
            <InfiniteScroll canFetchOnIdle={!isRequestPending}>
              {() => (
                <div className={styles.list}>
                  {resources.map((resource) => (
                    <Resource
                      key={resource.uuid}
                      resource={resource}
                      isSelected={isSelected(resource)}
                      select={() => selectResources([resource])}
                      unSelect={() => unSelectResources([resource])}
                    />
                  ))}
                  {isRequestPending && <Loader />}
                  <Flag
                    name="filterResourcesFlag"
                    retrieveItems={() =>
                      !isRequestPending &&
                      retrieveFilterResources({
                        planzone,
                        project,
                        filter: resourcesFilter,
                      })
                    }
                    isDisabled={
                      isRequestPending ||
                      resourcesFilter.page === 0 ||
                      resourcesFilter.page >= totalPages
                    }
                  />
                </div>
              )}
            </InfiniteScroll>
          </div>
        }
        isOpen={isOpen}
      />
    </div>
  );
}

export { Resources };
